import React from "react";
import { CreditCard, AlertTriangle } from "lucide-react";
import { AssignCardAction, MoveCompanyAction } from "@/components/InboxCheckActions";
import { ExpenseClaimAction } from "@/components/inbox/ExpenseClaimAction";
import { CardOriginHint } from "@/components/inbox/CardOriginHint";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

/** Roter Balken oben am Beleg: Karte ist weder Firmenkarte noch bekannte Privatkarte (oder gehört einer anderen Firma). */
export function UnknownCardBanner({ companyId, item, onUpdated, onRefresh, onNote, privateCompanies = [], isPrivate = false }) {
  const navigate = useNavigate();
  const [privBusy, setPrivBusy] = React.useState(false);
  const moveToPrivate = async (pc0) => {
    setPrivBusy(true);
    try {
      await apiClient.post(`/companies/${pc0.id}/cards`, { last4: item.extracted?.payment_card?.last4, brand: item.extracted?.payment_card?.brand || null, kind: "company", label: `Privatkarte (${pc0.name})` }).catch(() => {});
      await apiClient.post(`/companies/${companyId}/inbox/${item.id}/move-company`, { target_company_id: pc0.id });
      toast.success(`Privater Beleg → «${pc0.name}» verschoben · Karte …${item.extracted?.payment_card?.last4} dort gemerkt (künftige Belege werden automatisch erkannt)`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Verschieben fehlgeschlagen"); }
    finally { setPrivBusy(false); }
  };
  const pc = item.extracted?.payment_card;
  if (!pc || pc.method !== "card" || !pc.last4) return null;
  if (item.source_meta?.expense_claim) return null;
  if (pc.scope === "company" || pc.scope === "employee_private") return null;
  const other = pc.scope === "other_company";
  const label = `${pc.brand || "Karte"} …${pc.last4}`;
  return (
    <div className="-mx-4 -mt-4 mb-3 rounded-t-xl border-b border-rose-300 bg-rose-600 px-4 py-2 text-white" data-testid={`inbox-unknown-card-banner-${item.id}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-semibold inline-flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> {other ? `Karte von «${pc.other_company_name}»` : `Fremde Karte ${label}`}</span>
        <span className="opacity-90">
          {other
            ? (isPrivate ? "– gehört einer deiner Firmen: Beleg dort als Spesen erfassen (unten «Doch Firmenaufwand?»)." : "– gehört einer anderen eigenen Firma: Beleg dort erfassen oder weiterbelasten.")
            : isPrivate ? "– noch nicht als eigene Karte hinterlegt. Mit «Karte zuordnen» merken, dann werden künftige Belege automatisch erkannt."
            : "– nicht als Firmenkarte hinterlegt. Von einem Mitarbeitenden bezahlt? Dann als Spesen erfassen (Rückerstattung im nächsten Lohnlauf)."}
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-1.5 max-w-full [&_button]:border-white/70 [&_button]:bg-white [&_button]:text-rose-800 [&_button:hover]:bg-rose-50 [&>div]:bg-white [&>div]:text-slate-800 [&>div]:border-slate-200 [&>div]:min-w-[30rem] [&>div_button]:border-amber-500 [&>div_button]:bg-amber-600 [&>div_button]:text-white [&>div_button:hover]:bg-amber-700">
          {!other && !isPrivate && <ExpenseClaimAction companyId={companyId} item={item} onUpdated={onUpdated} compact />}
          <a href="/settings#section-cards" className="ml-auto text-[11px] underline underline-offset-2 opacity-80 hover:opacity-100" data-testid={`inbox-cards-settings-link-${item.id}`}>Karten verwalten</a>
          {other
            ? <MoveCompanyAction companyId={companyId} item={item} onRefresh={onRefresh} />
            : <AssignCardAction companyId={companyId} item={item} onUpdated={onUpdated} onRefresh={onRefresh} />}
          {!other && !isPrivate && (
            <button type="button" onClick={() => onNote?.(item)} className="inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] font-medium" data-testid={`inbox-unknown-card-note-${item.id}`}>
              Notiz «bezahlt von …»
            </button>
          )}
          {!other && (
            <a href="/settings#section-cards" className="inline-flex items-center gap-1 rounded border border-white/70 px-1.5 py-px text-[10px] font-medium text-white underline-offset-2 hover:underline" title="Alle Firmen- und Privatkarten verwalten" data-testid={`inbox-unknown-card-settings-${item.id}`}>
              Firmenkarten verwalten
            </a>
          )}
        </span>
      </div>
      {item.card_origin && <div className="mt-2 text-slate-900"><CardOriginHint companyId={companyId} item={item} onDone={onRefresh} /></div>}
      {!item.card_origin && !other && !isPrivate && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`inbox-private-options-${item.id}`}>
          <span className="opacity-90">Privat bezahlt und kein Firmenaufwand?</span>
          {privateCompanies.map((pc0) => (
            <button key={pc0.id} type="button" disabled={privBusy} onClick={() => moveToPrivate(pc0)} className="inline-flex items-center gap-1 rounded border border-white/70 bg-white px-1.5 py-px text-[10px] font-medium text-rose-800 hover:bg-rose-50" data-testid={`inbox-move-private-${item.id}-${pc0.id}`}>
              → Privat «{pc0.name}» (Karte dort merken)
            </button>
          ))}
          {privateCompanies.length === 0 && (
            <button type="button" onClick={() => navigate(`/new-company?type=privat&name=${encodeURIComponent("Privat")}`)} className="inline-flex items-center gap-1 rounded border border-white/70 bg-white px-1.5 py-px text-[10px] font-medium text-rose-800 hover:bg-rose-50" title="Eigene Privat-Buchhaltung (ohne MWST) anlegen – private Belege und Karten werden dann dorthin verschoben und künftig automatisch erkannt" data-testid={`inbox-create-private-${item.id}`}>
              Privatkonto anlegen (z. B. «Privat Alex»)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
